import React from 'react'
import { flowPhaseLabel } from '@/lib/statusLabels'

export type StatusType = 'Ready' | 'Progressing' | 'Error' | 'Pending' | 'Failed'

interface StatusLabelProps {
  status: StatusType
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}

const getStatusConfig = (status: StatusType) => {
  switch (status) {
    case 'Ready':
      return {
        color: 'bg-green-500 text-white',
        icon: (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ),
        label: flowPhaseLabel(status)
      }
    case 'Progressing':
    case 'Pending':
      return {
        color: 'bg-highlight-yellow text-gray-800',
        icon: (
          <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
        label: flowPhaseLabel(status)
      }
    case 'Error':
    case 'Failed':
      return {
        color: 'bg-red-500 text-white',
        icon: (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ),
        label: flowPhaseLabel(status)
      }
    default:
      return {
        color: 'bg-gray-500 text-white',
        icon: (
          <div className="w-3 h-3 bg-white rounded-full opacity-70"></div>
        ),
        label: flowPhaseLabel(status)
      }
  }
}

const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return 'px-2 py-1 text-xs'
    case 'lg':
      return 'px-4 py-2 text-sm'
    case 'md':
    default:
      return 'px-3 py-1.5 text-xs'
  }
}

export default function StatusLabel({
  status,
  size = 'md',
  showIcon = true,
  className = ''
}: StatusLabelProps) {
  const config = getStatusConfig(status)
  const sizeClasses = getSizeClasses(size)

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium transition-all duration-200 ${config.color} ${sizeClasses} ${className}`}
    >
      {showIcon && (
        <span className="mr-1.5 flex-shrink-0">
          {config.icon}
        </span>
      )}
      {config.label}
    </span>
  )
}